import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';
import { formatNumber, fPercent } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';

interface FunnelKpiCardProps {
  title: string;
  total: number;
  compare?: number;
  definition?: string;
  isPercent?: boolean;
  isCurrency?: boolean;
  invertedColor?: boolean;
  isFetching?: boolean;
  /** Masque le badge d'évolution quand la variation est infinie (base de compare = 0).
   *  Opt-in par carte : la carte Savings passe souvent de/vers 0, d'où un "+∞" trop fréquent. */
  hideInfinity?: boolean;
}

export default function FunnelKpiCard({
  title,
  total,
  compare,
  definition,
  isPercent,
  isCurrency,
  invertedColor,
  isFetching,
  hideInfinity,
}: FunnelKpiCardProps) {
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace: any = useCurrentWorkspace();
  const locale = localStorage.getItem('i18nextLng') || 'en';
  const currency = workspace?.currency || 'USD';

  const formatValue = (value: number) => {
    if (isPercent) return fPercent(value);
    if (isCurrency) return formatCurrency(value, currency, locale, true, 2);
    return formatNumber(value);
  };

  const comparePercent = (a: number, b?: number) => {
    if (b == null) return undefined;
    // A compare period without data arrives as exactly 0 from the API: keep the
    // badge (±∞) instead of silently dropping it like a missing compare.
    if (b === 0) return a === 0 ? 0 : a > 0 ? Infinity : -Infinity;
    return ((a - b) / b) * 100;
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });

  const diff = comparePercent(total, compare);

  const getChangeColor = () => {
    if (diff === undefined || isNaN(diff)) return '#637381';
    if (diff === 0) return '#637381';
    if (diff < 0) return invertedColor ? theme.palette.success.main : theme.palette.error.main;
    return invertedColor ? theme.palette.error.main : theme.palette.success.main;
  };

  const formatDiff = () => {
    if (diff === undefined || isNaN(diff)) return '+∞';
    if (!isFinite(diff)) return diff > 0 ? '+∞' : '-∞';
    if (diff === 0) return fPercent(0);
    return diff > 0 ? `+${fPercent(diff)}` : fPercent(diff);
  };

  if (isFetching) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={80}
        animation="wave"
        sx={{ borderRadius: '12px' }}
      />
    );
  }

  return (
    <Stack
      sx={{
        minHeight: 80,
        px: '16px',
        py: '12px',
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#EAEAEA'}`,
        borderRadius: '12px',
        gap: '6px',
        justifyContent: 'center',
      }}
    >
      {/* Row 1 — title + diff */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: '#637381',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
          {definition && (
            <Tooltip
              title={definition}
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#ffffff',
                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#637381',
                    fontSize: '12px',
                    fontFamily: 'Public Sans, Helvetica',
                    fontWeight: 600,
                    lineHeight: '18px',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    boxShadow: theme.customShadows.z16,
                  },
                },
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Iconify icon="eva:info-outline" width={12} height={12} sx={{ color: 'text.disabled' }} />
              </Box>
            </Tooltip>
          )}
        </Stack>

        {filterStore.compare && diff !== undefined && !(hideInfinity && !isFinite(diff)) && (
          <Typography sx={{ fontSize: '12px', fontWeight: 500, lineHeight: '14.4px', color: getChangeColor(), whiteSpace: 'nowrap' }}>
            {formatDiff()}
          </Typography>
        )}
      </Stack>

      {/* Row 2 — values (+ dates in compare mode) */}
      {filterStore.compare ? (
        <Box display="flex" flexDirection="column" gap="4px">
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#333C47', fontSize: '20px', fontWeight: 700, lineHeight: 1 }}>
              {formatValue(total ?? 0)}
            </Typography>
            <Box display="flex" flexDirection="row" alignItems="baseline" gap="6px">
              <Typography sx={{ fontSize: '11px', color: '#919EAB', lineHeight: '12px', fontWeight: 400 }}>
                vs.
              </Typography>
              <Typography sx={{ color: '#919EAB', fontSize: '20px', fontWeight: 700, lineHeight: 1 }}>
                {formatValue(compare ?? 0)}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Typography sx={{ fontSize: '11px', color: '#919EAB', lineHeight: '12px', fontWeight: 400 }}>
              {filterStore?.start && filterStore?.end
                ? `${formatDate(filterStore.start)} - ${formatDate(filterStore.end)}`
                : ''}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#919EAB', lineHeight: '12px', fontWeight: 400 }}>
              {filterStore?.compareStart && filterStore?.compareEnd
                ? `${formatDate(filterStore.compareStart)} - ${formatDate(filterStore.compareEnd)}`
                : ''}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography
          sx={{
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#333C47',
            fontSize: '24px',
            fontWeight: 700,
            lineHeight: '28.8px',
          }}
        >
          {formatValue(total ?? 0)}
        </Typography>
      )}
    </Stack>
  );
}
